/**
 * Shared Mermaid initialize() options for preview iframe and Puppeteer PDF.
 * Keep these identical so diagram geometry (and thus pagination) stays aligned.
 *
 * Diagram type size tracks diagramFontSize (pt → px 1:1). Font family comes from
 * diagramFontFamily (with the same script fallbacks as body text). useMaxWidth is
 * off so Mermaid emits intrinsic pixel sizes that grow/shrink with fontSize; print
 * CSS still applies max-width: 100% so wide diagrams fit the page without breaking
 * preview/PDF parity. At 8pt diagrams shrink so they can share a page with nearby
 * headings under wide margins instead of being pushed alone onto page 2.
 */

import { pickWcagAaTextColor } from "@/src/lib/pdf/print-document/color-contrast";
import {
  defaultPdfDiagramTheme,
  getPdfThemeDefinition,
  resolveDiagramThemePalette,
  type PdfDiagramTheme,
  type PdfTheme,
} from "@/src/lib/pdf/print-document/themes";

const intrinsicWidth = { useMaxWidth: false } as const;

export type BuildMermaidInitializeConfigOptions = {
  fontFamily?: string;
  fontSizePt?: number;
  theme?: PdfTheme;
  diagramTheme?: PdfDiagramTheme;
};

export function buildMermaidInitializeConfig(
  fontFamily?: string,
  fontSizePt = 12,
  theme: PdfTheme = "github",
  diagramTheme: PdfDiagramTheme = defaultPdfDiagramTheme,
) {
  // Use pt as px so 12pt → 12px (near Mermaid's 16px default) and 8pt → 8px.
  // The CSS px≈pt mapping at 96dpi (12pt→16px) keeps diagrams too tall at 8pt
  // wide-margin layouts; matching the numeric pt value packs closer to body text.
  const fontSizePx = Math.max(8, Math.round(fontSizePt));
  const fontSize = `${fontSizePx}px`;
  // Tighten flowchart chrome proportionally so node boxes shrink with the type.
  const spacing = Math.max(8, Math.round(fontSizePx * 1.25));
  const padding = Math.max(4, Math.round(fontSizePx * 0.5));
  const paletteTheme = resolveDiagramThemePalette(theme, diagramTheme);
  const themeDefinition = getPdfThemeDefinition(paletteTheme);
  const documentTheme = getPdfThemeDefinition(theme);
  // Node fills come from the diagram palette; legend/title/labels sit on the
  // print page, so pull those text colors from the document theme and enforce
  // WCAG AA contrast against the page background.
  const pageSurfaceText = pickWcagAaTextColor(
    documentTheme.tokens.page,
    documentTheme.tokens.text,
    "#24292f",
    "#f8f8f2",
  );
  const pageSurfaceHeading = pickWcagAaTextColor(
    documentTheme.tokens.page,
    documentTheme.tokens.heading,
    "#0f172a",
    "#f8f8f2",
  );
  const themeVariables: Record<string, string> = {
    fontSize,
    ...themeDefinition.mermaid.themeVariables,
    textColor: pageSurfaceText,
    titleColor: pageSurfaceHeading,
    pieTitleTextColor: pageSurfaceHeading,
    pieLegendTextColor: pageSurfaceText,
    // Edge labels sit on the page surface, not the diagram node fill.
    edgeLabelBackground: documentTheme.tokens.page,
  };

  return {
    flowchart: {
      htmlLabels: false,
      useMaxWidth: false,
      nodeSpacing: spacing,
      rankSpacing: spacing,
      padding,
    },
    // Mermaid defaults these to useMaxWidth: true; keep them intrinsic so type
    // scales with document font the same way as flowcharts.
    sequence: intrinsicWidth,
    class: intrinsicWidth,
    state: intrinsicWidth,
    er: intrinsicWidth,
    pie: intrinsicWidth,
    gantt: intrinsicWidth,
    mindmap: intrinsicWidth,
    gitGraph: intrinsicWidth,
    journey: intrinsicWidth,
    timeline: intrinsicWidth,
    fontSize: fontSizePx,
    securityLevel: "strict" as const,
    startOnLoad: false,
    theme: themeDefinition.mermaid.theme,
    themeVariables,
    ...(fontFamily ? { fontFamily } : {}),
  };
}
